import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { ApplicationState } from 'core/redux/reducers/rootReducer';
import type React from 'react';
import { useEffect,useRef } from 'react';
import { useSelector } from 'react-redux';
import { handleZoomableElementsChange } from 'sequenceAnalyser/components/Sequences/SequenceTable/SequenceTable';
import { elementToScale } from 'sequenceAnalyser/utilities/screen/screen';

import classes from './SequenceTableVisualisationWrapper.style';

interface ComponentProps {
  width: number;
  height: number;
  sidebarPixelWidth: number;
  title: string;
  children: React.ReactNode;
  sidebarActions?: React.ReactNode;
}

function SequenceTableVisualisationWrapper({
  children, title, width, height, sidebarPixelWidth, sidebarActions,
}: ComponentProps) {
  const ref = useRef<HTMLDivElement>(null);

  const zoomLevel = useSelector((state: ApplicationState) => state.sequenceAnalyser.zoomLevel);

  const adjustedheight = elementToScale(height, zoomLevel);

  // The element is positioned fixed so it can be scrolled horizontally
  // We want it to be sticky on vertical scroll so we adjust its 'left' value on scroll
  const handleScroll = () => {
    if (ref && ref.current) {
      const left = window.scrollX;
      ref.current.style.left = `-${left}px`;
    }
  };

  useEffect(() => {
    handleScroll();
    window.addEventListener('scroll', handleScroll);
    handleZoomableElementsChange(zoomLevel);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // zoomLevel: removed as we only want to add it once
  ]);

  const mb = height > adjustedheight ? (height + 1 - adjustedheight) * -1 : (adjustedheight - height - 1);

  return (
    <Box
      sx={{
        ...classes.root,
        width: `${width}px`,
        marginBottom: `${mb}px`,
      }}
    >
      <Box
        sx={{
          ...classes.sidebar,
          width: `${sidebarPixelWidth}px`,
        }}
        className="zoomable"
      >
        <Box display="flex">
          <Typography sx={{ flexGrow: 1 }}>{title}</Typography>
          {sidebarActions}
        </Box>
      </Box>
      <Box
        ref={ref}
        sx={{
          ...classes.content,
          transformOrigin: `-${sidebarPixelWidth}px 0`,
        }}
        className="zoomable"
      >
        {children}
      </Box>
    </Box>
  );
}

export default SequenceTableVisualisationWrapper;
